/**
 * Mock Invoice Templates
 * 
 * Pre-built templates for the template library
 */

import {
  createDefaultTemplate,
  DEFAULT_COLUMNS,
} from './invoice-template-types';
import type {
  InvoiceTemplate,
  InvoicePreviewData,
  TemplateLibraryItem,
} from './invoice-template-types';

// ============================================================================
// MOCK TEMPLATES
// ============================================================================

export const mockTemplates: InvoiceTemplate[] = [
  // 1. Professional Detailed
  {
    id: 1,
    name: 'Professional Detailed',
    description: 'Comprehensive invoice with all time entry details',
    isDefault: true,
    sections: [
      {
        id: 'header-1',
        type: 'header',
        order: 0,
        visible: true,
        config: {
          type: 'header',
          showCompanyName: true,
          showCompanyAddress: true,
          showCompanyPhone: true,
          showCompanyEmail: true,
          showVAT: true,
          showRegistration: true,
          showInvoiceNumber: true,
          showInvoiceDate: true,
          showDueDate: true,
          showPONumber: true,
          showClientName: true,
          showClientAddress: true,
          showClientContact: true,
          layout: 'two-column',
        },
      },
      {
        id: 'summary-1',
        type: 'summary',
        order: 1,
        visible: true,
        config: {
          type: 'summary',
          showProjectName: true,
          showProjectDescription: true,
          showBillingPeriod: true,
          showTotalHours: true,
          showTotalAmount: true,
        },
      },
      {
        id: 'line-items-1',
        type: 'line-items',
        order: 2,
        visible: true,
        config: {
          type: 'line-items',
          groupBy: 'none',
          showGroupSubtotals: false,
          expandable: false,
          columns: DEFAULT_COLUMNS,
          showBorders: true,
          alternateRowColors: true,
          headerBackgroundColor: '#f1f5f9',
        },
      },
      {
        id: 'totals-1',
        type: 'totals',
        order: 3,
        visible: true,
        config: {
          type: 'totals',
          showSubtotal: true,
          showTax: true,
          showDiscount: false,
          showTotal: true,
          taxLabel: 'VAT (20%)',
          taxRate: 20,
          alignment: 'right',
          showCurrency: true,
        },
      },
      {
        id: 'footer-1',
        type: 'footer',
        order: 4,
        visible: true,
        config: {
          type: 'footer',
          showBankDetails: true,
          showNotes: true,
          showTermsAndConditions: true,
          paymentTerms: 'Payment due within 30 days of invoice date',
          bankDetails: 'Bank: Example Bank\nAccount: 1234567890\nRouting: 987654321',
          notes: 'Thank you for your business!',
          termsAndConditions: 'All work performed is subject to our standard terms and conditions.',
          layout: 'two-column',
        },
      },
    ],
    branding: {
      logoPosition: 'left',
      logoWidth: 150,
      primaryColor: '#2563eb',
      secondaryColor: '#64748b',
      textColor: '#1e293b',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 14,
    },
    settings: {
      groupBy: 'none',
      showIndividualEntries: true,
      dateFormat: 'MM/DD/YYYY',
      currencyFormat: 'USD',
      numberFormat: '1,234.56',
      pageSize: 'Letter',
      orientation: 'portrait',
      margins: { top: 72, right: 72, bottom: 72, left: 72 },
    },
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // 2. Summary Invoice
  {
    id: 2,
    name: 'Summary Invoice',
    description: 'High-level summary without individual time entries',
    isDefault: false,
    sections: [
      {
        id: 'header-2',
        type: 'header',
        order: 0,
        visible: true,
        config: {
          type: 'header',
          showCompanyName: true,
          showCompanyAddress: true,
          showCompanyPhone: false,
          showCompanyEmail: true,
          showVAT: true,
          showRegistration: true,
          showInvoiceNumber: true,
          showInvoiceDate: true,
          showDueDate: true,
          showPONumber: false,
          showClientName: true,
          showClientAddress: true,
          showClientContact: false,
          layout: 'two-column',
        },
      },
      {
        id: 'summary-2',
        type: 'summary',
        order: 1,
        visible: true,
        config: {
          type: 'summary',
          showProjectName: true,
          showProjectDescription: false,
          showBillingPeriod: true,
          showTotalHours: true,
          showTotalAmount: true,
          customText: 'Professional services for {project.name} during {billing.period}',
        },
      },
      {
        id: 'line-items-2',
        type: 'line-items',
        order: 2,
        visible: true,
        config: {
          type: 'line-items',
          groupBy: 'phase',
          showGroupSubtotals: true,
          expandable: false,
          columns: [
            {
              id: 'description',
              field: 'description',
              label: 'Description',
              visible: true,
              width: 55,
              alignment: 'left',
            },
            {
              id: 'qty',
              field: 'qty',
              label: 'Qty',
              visible: true,
              width: 15,
              alignment: 'right',
              format: '0.00',
            },
            {
              id: 'amount',
              field: 'amount',
              label: 'Amount',
              visible: true,
              width: 30,
              alignment: 'right',
              format: '£0,0.00',
            },
          ],
          showBorders: true,
          alternateRowColors: false,
          headerBackgroundColor: '#f1f5f9',
        },
      },
      {
        id: 'totals-2',
        type: 'totals',
        order: 3,
        visible: true,
        config: {
          type: 'totals',
          showSubtotal: true,
          showTax: false,
          showDiscount: false,
          showTotal: true,
          alignment: 'right',
          showCurrency: true,
        },
      },
      {
        id: 'footer-2',
        type: 'footer',
        order: 4,
        visible: true,
        config: {
          type: 'footer',
          showBankDetails: false,
          showNotes: true,
          showTermsAndConditions: false,
          paymentTerms: 'Net 30',
          notes: 'Thank you for your business!',
          layout: 'single-column',
        },
      },
    ],
    branding: {
      logoPosition: 'center',
      logoWidth: 180,
      primaryColor: '#059669',
      secondaryColor: '#64748b',
      textColor: '#1e293b',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 14,
    },
    settings: {
      groupBy: 'phase',
      showIndividualEntries: false,
      dateFormat: 'MM/DD/YYYY',
      currencyFormat: 'USD',
      numberFormat: '1,234.56',
      pageSize: 'Letter',
      orientation: 'portrait',
      margins: { top: 72, right: 72, bottom: 72, left: 72 },
    },
    createdAt: new Date('2024-01-02'),
    updatedAt: new Date('2024-01-02'),
  },

  // 3. Grouped by Person
  {
    id: 3,
    name: 'Grouped by Person',
    description: 'Time entries organized by team member',
    isDefault: false,
    sections: [
      {
        id: 'header-3',
        type: 'header',
        order: 0,
        visible: true,
        config: {
          type: 'header',
          showCompanyName: true,
          showCompanyAddress: true,
          showCompanyPhone: true,
          showCompanyEmail: true,
          showVAT: true,
          showRegistration: true,
          showInvoiceNumber: true,
          showInvoiceDate: true,
          showDueDate: true,
          showPONumber: false,
          showClientName: true,
          showClientAddress: true,
          showClientContact: true,
          layout: 'two-column',
        },
      },
      {
        id: 'line-items-3',
        type: 'line-items',
        order: 1,
        visible: true,
        config: {
          type: 'line-items',
          groupBy: 'person',
          showGroupSubtotals: true,
          expandable: true,
          columns: [
            {
              id: 'date',
              field: 'date',
              label: 'Date',
              visible: true,
              width: 15,
              alignment: 'left',
              format: 'MM/DD/YYYY',
            },
            {
              id: 'description',
              field: 'description',
              label: 'Description',
              visible: true,
              width: 40,
              alignment: 'left',
            },
            {
              id: 'role',
              field: 'role',
              label: 'Role',
              visible: true,
              width: 15,
              alignment: 'left',
            },
            {
              id: 'qty',
              field: 'qty',
              label: 'Qty',
              visible: true,
              width: 10,
              alignment: 'right',
              format: '0.00',
            },
            {
              id: 'net',
              field: 'net',
              label: 'Net',
              visible: true,
              width: 15,
              alignment: 'right',
              format: '£0,0.00',
            },
            {
              id: 'amount',
              field: 'amount',
              label: 'Amount',
              visible: true,
              width: 10,
              alignment: 'right',
              format: '£0,0.00',
            },
          ],
          showBorders: true,
          alternateRowColors: true,
          headerBackgroundColor: '#f1f5f9',
        },
      },
      {
        id: 'totals-3',
        type: 'totals',
        order: 2,
        visible: true,
        config: {
          type: 'totals',
          showSubtotal: true,
          showTax: true,
          showDiscount: false,
          showTotal: true,
          taxLabel: 'VAT (20%)',
          taxRate: 20,
          alignment: 'right',
          showCurrency: true,
        },
      },
      {
        id: 'footer-3',
        type: 'footer',
        order: 3,
        visible: true,
        config: {
          type: 'footer',
          showBankDetails: false,
          showNotes: true,
          showTermsAndConditions: false,
          paymentTerms: 'Payment due within 30 days',
          notes: 'Thank you for your business!',
          layout: 'single-column',
        },
      },
    ],
    branding: {
      logoPosition: 'left',
      logoWidth: 150,
      primaryColor: '#7c3aed',
      secondaryColor: '#64748b',
      textColor: '#1e293b',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 14,
    },
    settings: {
      groupBy: 'person',
      showIndividualEntries: true,
      dateFormat: 'MM/DD/YYYY',
      currencyFormat: 'USD',
      numberFormat: '1,234.56',
      pageSize: 'Letter',
      orientation: 'portrait',
      margins: { top: 72, right: 72, bottom: 72, left: 72 },
    },
    createdAt: new Date('2024-01-03'),
    updatedAt: new Date('2024-01-03'),
  },

  // 4. Client-Friendly (No Rates)
  {
    id: 4,
    name: 'Client-Friendly (No Rates)',
    description: 'Detailed invoice without showing hourly rates',
    isDefault: false,
    sections: [
      {
        id: 'header-4',
        type: 'header',
        order: 0,
        visible: true,
        config: {
          type: 'header',
          showCompanyName: true,
          showCompanyAddress: true,
          showCompanyPhone: true,
          showCompanyEmail: true,
          showVAT: true,
          showRegistration: true,
          showInvoiceNumber: true,
          showInvoiceDate: true,
          showDueDate: true,
          showPONumber: false,
          showClientName: true,
          showClientAddress: true,
          showClientContact: true,
          layout: 'two-column',
        },
      },
      {
        id: 'line-items-4',
        type: 'line-items',
        order: 1,
        visible: true,
        config: {
          type: 'line-items',
          groupBy: 'none',
          showGroupSubtotals: false,
          expandable: false,
          columns: [
            {
              id: 'date',
              field: 'date',
              label: 'Date',
              visible: true,
              width: 15,
              alignment: 'left',
              format: 'MM/DD/YYYY',
            },
            {
              id: 'description',
              field: 'description',
              label: 'Description',
              visible: true,
              width: 45,
              alignment: 'left',
            },
            {
              id: 'person',
              field: 'person',
              label: 'Person',
              visible: true,
              width: 20,
              alignment: 'left',
            },
            {
              id: 'qty',
              field: 'qty',
              label: 'Qty',
              visible: true,
              width: 10,
              alignment: 'right',
              format: '0.00',
            },
            {
              id: 'amount',
              field: 'amount',
              label: 'Amount',
              visible: true,
              width: 10,
              alignment: 'right',
              format: '£0,0.00',
            },
          ],
          showBorders: true,
          alternateRowColors: true,
          headerBackgroundColor: '#f1f5f9',
        },
      },
      {
        id: 'totals-4',
        type: 'totals',
        order: 2,
        visible: true,
        config: {
          type: 'totals',
          showSubtotal: true,
          showTax: false,
          showDiscount: false,
          showTotal: true,
          alignment: 'right',
          showCurrency: true,
        },
      },
      {
        id: 'footer-4',
        type: 'footer',
        order: 3,
        visible: true,
        config: {
          type: 'footer',
          showBankDetails: false,
          showNotes: true,
          showTermsAndConditions: false,
          paymentTerms: 'Payment due within 30 days',
          notes: 'Thank you for your business!',
          layout: 'single-column',
        },
      },
    ],
    branding: {
      logoPosition: 'left',
      logoWidth: 150,
      primaryColor: '#dc2626',
      secondaryColor: '#64748b',
      textColor: '#1e293b',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 14,
    },
    settings: {
      groupBy: 'none',
      showIndividualEntries: true,
      dateFormat: 'MM/DD/YYYY',
      currencyFormat: 'USD',
      numberFormat: '1,234.56',
      pageSize: 'Letter',
      orientation: 'portrait',
      margins: { top: 72, right: 72, bottom: 72, left: 72 },
    },
    createdAt: new Date('2024-01-04'),
    updatedAt: new Date('2024-01-04'),
  },

  // 5. Minimal Clean
  {
    id: 5,
    name: 'Minimal Clean',
    description: 'Simple, clean invoice with essential information only',
    isDefault: false,
    sections: [
      {
        id: 'header-5',
        type: 'header',
        order: 0,
        visible: true,
        config: {
          type: 'header',
          showCompanyName: true,
          showCompanyAddress: false,
          showCompanyPhone: false,
          showCompanyEmail: true,
          showVAT: true,
          showRegistration: true,
          showInvoiceNumber: true,
          showInvoiceDate: true,
          showDueDate: true,
          showPONumber: false,
          showClientName: true,
          showClientAddress: false,
          showClientContact: false,
          layout: 'single-column',
        },
      },
      {
        id: 'line-items-5',
        type: 'line-items',
        order: 1,
        visible: true,
        config: {
          type: 'line-items',
          groupBy: 'none',
          showGroupSubtotals: false,
          expandable: false,
          columns: [
            {
              id: 'description',
              field: 'description',
              label: 'Description',
              visible: true,
              width: 60,
              alignment: 'left',
            },
            {
              id: 'qty',
              field: 'qty',
              label: 'Qty',
              visible: true,
              width: 15,
              alignment: 'right',
              format: '0.00',
            },
            {
              id: 'amount',
              field: 'amount',
              label: 'Amount',
              visible: true,
              width: 25,
              alignment: 'right',
              format: '£0,0.00',
            },
          ],
          showBorders: false,
          alternateRowColors: false,
        },
      },
      {
        id: 'totals-5',
        type: 'totals',
        order: 2,
        visible: true,
        config: {
          type: 'totals',
          showSubtotal: false,
          showTax: false,
          showDiscount: false,
          showTotal: true,
          alignment: 'right',
          showCurrency: true,
        },
      },
      {
        id: 'footer-5',
        type: 'footer',
        order: 3,
        visible: true,
        config: {
          type: 'footer',
          showBankDetails: false,
          showNotes: false,
          showTermsAndConditions: false,
          paymentTerms: 'Net 30',
          layout: 'single-column',
        },
      },
    ],
    branding: {
      logoPosition: 'center',
      logoWidth: 120,
      primaryColor: '#0891b2',
      secondaryColor: '#64748b',
      textColor: '#1e293b',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 14,
    },
    settings: {
      groupBy: 'none',
      showIndividualEntries: true,
      dateFormat: 'MM/DD/YYYY',
      currencyFormat: 'USD',
      numberFormat: '1,234.56',
      pageSize: 'Letter',
      orientation: 'portrait',
      margins: { top: 72, right: 72, bottom: 72, left: 72 },
    },
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-05'),
  },
];

// ============================================================================
// TEMPLATE LIBRARY
// ============================================================================

export const templateLibrary: TemplateLibraryItem[] = [
  {
    id: 'professional-detailed',
    name: 'Professional Detailed',
    description: 'Comprehensive invoice with all time entry details',
    category: 'professional-services',
    thumbnail: '/templates/professional-detailed.png',
    template: mockTemplates[0],
  },
  {
    id: 'summary-invoice',
    name: 'Summary Invoice',
    description: 'High-level summary without individual time entries',
    category: 'consulting',
    thumbnail: '/templates/summary-invoice.png',
    template: mockTemplates[1],
  },
  {
    id: 'grouped-by-person',
    name: 'Grouped by Person',
    description: 'Time entries organized by team member',
    category: 'professional-services',
    thumbnail: '/templates/grouped-by-person.png',
    template: mockTemplates[2],
  },
  {
    id: 'client-friendly',
    name: 'Client-Friendly (No Rates)',
    description: 'Detailed invoice without showing hourly rates',
    category: 'general',
    thumbnail: '/templates/client-friendly.png',
    template: mockTemplates[3],
  },
  {
    id: 'minimal-clean',
    name: 'Minimal Clean',
    description: 'Simple, clean invoice with essential information only',
    category: 'general',
    thumbnail: '/templates/minimal-clean.png',
    template: mockTemplates[4],
  },
];

// ============================================================================
// MOCK PREVIEW DATA
// ============================================================================

export const mockPreviewData: InvoicePreviewData = {
  company: {
    name: 'Acme Professional Services Ltd',
    address: '123 High Street, Suite 100\nLondon, EC1A 1BB\nUnited Kingdom',
    phone: '+44 20 7123 4567',
    email: 'billing@acmepro.co.uk',
    logo: undefined, // Will use placeholder
    vatNumber: 'GB123456789',
    registrationNumber: 'RC123456',
  },
  invoice: {
    number: 'INV-2024-001',
    date: '15/01/2024',
    dueDate: '14/02/2024',
    poNumber: 'PO-12345',
  },
  client: {
    name: 'Tech Innovations Ltd',
    address: '456 Oxford Street\nManchester, M1 1AA\nUnited Kingdom',
    contact: 'John Smith, CFO',
  },
  project: {
    name: 'Website Redesign Project',
    description: 'Complete redesign and development of corporate website',
  },
  billingPeriod: {
    start: '01/01/2024',
    end: '31/01/2024',
  },
  lineItems: [
    // Time Entries
    {
      id: '1',
      lineItemType: 'timesheet' as const,
      date: '02/01/2024',
      description: 'Initial project planning and requirements gathering',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: 'Sarah Johnson',
      role: 'Project Manager',
      phase: 'Planning',
      task: 'Requirements',
      qty: 4.0,
      net: 500.00,
      vat: 100.00,
      vatCode: 'S',
      amount: 600.00,
    },
    {
      id: '2',
      lineItemType: 'timesheet' as const,
      date: '03/01/2024',
      description: 'UI/UX design mockups and wireframes',
      costCentreCode: 'CC-DES',
      costCentreName: 'Design',
      person: 'Mike Chen',
      role: 'Designer',
      phase: 'Design',
      task: 'Wireframes',
      qty: 8.0,
      net: 833.33,
      vat: 166.67,
      vatCode: 'S',
      amount: 1000.00,
    },
    {
      id: '3',
      lineItemType: 'timesheet' as const,
      date: '04/01/2024',
      description: 'Frontend development - homepage',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: 'Emily Rodriguez',
      role: 'Developer',
      phase: 'Development',
      task: 'Frontend',
      qty: 6.5,
      net: 758.33,
      vat: 151.67,
      vatCode: 'S',
      amount: 910.00,
    },
    {
      id: '4',
      lineItemType: 'timesheet' as const,
      date: '05/01/2024',
      description: 'Backend API development',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: 'David Kim',
      role: 'Developer',
      phase: 'Development',
      task: 'Backend',
      qty: 7.0,
      net: 816.67,
      vat: 163.33,
      vatCode: 'S',
      amount: 980.00,
    },
    {
      id: '5',
      lineItemType: 'timesheet' as const,
      date: '08/01/2024',
      description: 'Client review meeting and feedback session',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: 'Sarah Johnson',
      role: 'Project Manager',
      phase: 'Planning',
      task: 'Client Meeting',
      qty: 2.0,
      net: 250.00,
      vat: 50.00,
      vatCode: 'S',
      amount: 300.00,
    },
    {
      id: '6',
      lineItemType: 'timesheet' as const,
      date: '09/01/2024',
      description: 'Design revisions based on client feedback',
      costCentreCode: 'CC-DES',
      costCentreName: 'Design',
      person: 'Mike Chen',
      role: 'Designer',
      phase: 'Design',
      task: 'Revisions',
      qty: 5.0,
      net: 520.83,
      vat: 104.17,
      vatCode: 'S',
      amount: 625.00,
    },
    {
      id: '7',
      lineItemType: 'timesheet' as const,
      date: '10/01/2024',
      description: 'Frontend development - product pages',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: 'Emily Rodriguez',
      role: 'Developer',
      phase: 'Development',
      task: 'Frontend',
      qty: 8.0,
      net: 933.33,
      vat: 186.67,
      vatCode: 'S',
      amount: 1120.00,
    },
    {
      id: '8',
      lineItemType: 'timesheet' as const,
      date: '11/01/2024',
      description: 'Database schema design and implementation',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: 'David Kim',
      role: 'Developer',
      phase: 'Development',
      task: 'Backend',
      qty: 6.0,
      net: 700.00,
      vat: 140.00,
      vatCode: 'S',
      amount: 840.00,
    },
    // Purchases
    {
      id: '9',
      lineItemType: 'purchase' as const,
      date: '06/01/2024',
      description: 'Adobe Creative Cloud subscription (monthly)',
      costCentreCode: 'CC-IT',
      costCentreName: 'IT & Software',
      person: '', // Not applicable for purchases
      role: '',
      phase: 'Purchases',
      task: 'Software',
      qty: 1,
      net: 66.66,
      vat: 13.33,
      vatCode: 'S',
      amount: 79.99,
    },
    {
      id: '10',
      lineItemType: 'purchase' as const,
      date: '12/01/2024',
      description: 'Stock photography licenses (15 images)',
      costCentreCode: 'CC-DES',
      costCentreName: 'Design',
      person: '',
      role: '',
      phase: 'Purchases',
      task: 'Media',
      qty: 15,
      net: 124.88,
      vat: 24.97,
      vatCode: 'S',
      amount: 149.85,
    },
    {
      id: '11',
      lineItemType: 'purchase' as const,
      date: '15/01/2024',
      description: 'Premium font licenses for website',
      costCentreCode: 'CC-DES',
      costCentreName: 'Design',
      person: '',
      role: '',
      phase: 'Purchases',
      task: 'Fonts',
      qty: 3,
      net: 165.83,
      vat: 33.17,
      vatCode: 'S',
      amount: 199.00,
    },
    // Expenses
    {
      id: '12',
      lineItemType: 'expense' as const,
      date: '08/01/2024',
      description: 'Client meeting - lunch and transportation',
      costCentreCode: 'CC-DEV',
      costCentreName: 'Development',
      person: '', // Not applicable for expenses
      role: '',
      phase: 'Expenses',
      task: 'Client Meeting',
      qty: 1,
      net: 72.92,
      vat: 14.58,
      vatCode: 'S',
      amount: 87.50,
    },
    {
      id: '13',
      lineItemType: 'expense' as const,
      date: '14/01/2024',
      description: 'Printing and binding for project documentation',
      costCentreCode: 'CC-ADMIN',
      costCentreName: 'Administration',
      person: '',
      role: '',
      phase: 'Expenses',
      task: 'Documentation',
      qty: 1,
      net: 37.50,
      vat: 7.50,
      vatCode: 'S',
      amount: 45.00,
    },
    {
      id: '14',
      lineItemType: 'expense' as const,
      date: '18/01/2024',
      description: 'Cloud hosting services (AWS)',
      costCentreCode: 'CC-IT',
      costCentreName: 'IT & Software',
      person: '',
      role: '',
      phase: 'Expenses',
      task: 'Hosting',
      qty: 1,
      net: 104.17,
      vat: 20.83,
      vatCode: 'S',
      amount: 125.00,
    },
  ],
  totals: {
    subtotal: 7061.34, // Time entries (6375) + Purchases (428.84) + Expenses (257.50)
    tax: 1412.27, // 20% VAT (UK standard rate)
    discount: 0,
    total: 8473.61,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getTemplateById(id: number): InvoiceTemplate | undefined {
  return mockTemplates.find((t) => t.id === id);
}

export function getDefaultTemplate(): InvoiceTemplate {
  return mockTemplates.find((t) => t.isDefault) || mockTemplates[0];
}

export function getTemplatesByCategory(category: string): TemplateLibraryItem[] {
  return templateLibrary.filter((t) => t.category === category);
}

export function duplicateTemplate(template: InvoiceTemplate, newName: string): InvoiceTemplate {
  return {
    ...template,
    id: Date.now(),
    name: newName,
    isDefault: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}
